import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Directive,
  Injector,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {
  ControlValueAccessor,
  NgControl,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms';
import { MatLegacyAutocompleteSelectedEvent as MatAutocompleteSelectedEvent } from '@angular/material/legacy-autocomplete';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  startWith,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { getValidationErrorsChanges } from 'src/util/angular/ng-forms-validation';
import { isNullOrWhitespace } from 'src/util/string';

export interface AutocompleteHelperComponentConfig<TOptionValue, TControlValue> {
  placeholder: string;
  hint?: string;
  selectControlValue: (option: TOptionValue) => TControlValue;
  optionDisplayFn: (option: TOptionValue) => string;
  disableFn?: (option: TOptionValue) => boolean;
  getSingleOption: (value: TControlValue) => Observable<TOptionValue | undefined>;
  getManyOptions: (userInput: string) => Observable<TOptionValue[]>;
}

@Directive()
// eslint-disable-next-line @angular-eslint/directive-class-suffix
export abstract class AutcompleteComponentBase<TOptionValue, TControlValue>
  implements ControlValueAccessor, OnInit
{
  @Input()
  public placeholder: string;
  @Input()
  public hint?: string;

  public helperConfig: AutocompleteHelperComponentConfig<TOptionValue, TControlValue>;

  public abstract selectControlValue(option: TOptionValue): TControlValue;
  public abstract optionDisplayFn(option: TOptionValue): string;
  public abstract getSingleOption(value: TControlValue): Observable<TOptionValue | undefined>;
  public abstract getManyOptions(userInput: string): Observable<TOptionValue[]>;
  public abstract disableFn?(option: TOptionValue): boolean;

  public ngOnInit(): void {
    this.helperConfig = this;
  }

  public writeValue(obj: any): void {}
  public registerOnChange(fn: any): void {}
  public registerOnTouched(fn: any): void {}
}

@Component({
  selector: 'fl-autocomplete-helper',
  templateUrl: './autocomplete-helper.component.html',
  styleUrls: ['./autocomplete-helper.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AutocompleteHelperComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input()
  public config: AutocompleteHelperComponentConfig<any, any>;
  public options$: Observable<any[]>;
  public errors$: Observable<string[]>;
  public matFormFieldGroup: UntypedFormGroup;

  get autocompleteCtrl(): UntypedFormControl {
    return this.matFormFieldGroup.get('autocomplete') as UntypedFormControl;
  }
  public control: UntypedFormControl;

  public _onDestroy$ = new Subject<void>();

  public disableFn(option: any) {
    if (this.config.disableFn != null) {
      return this.config.disableFn(option);
    }
    return false;
  }

  constructor(
    private _cd: ChangeDetectorRef,
    private _injector: Injector,
    private _fb: UntypedFormBuilder,
  ) {
    this.matFormFieldGroup = this._fb.group({
      autocomplete: this._fb.control(null),
    });
  }

  public ngOnInit() {
    const ngControl: NgControl | null = this._injector.get(NgControl, null);
    if (ngControl != null) {
      this.control = ngControl.control as UntypedFormControl;
    } else {
      throw new Error(
        `Cannot find form control binding. Ensure the parent component 1) implements ControlValueAccessor 2) provides itself as an 'NG_VALUE_ACCESSOR' and 3) the grand-parent component (user of the autocomplete control), specifies a 'formControlName' or '[formControl]' directive on the autocomplete control.'`,
      );
    }

    this.control.statusChanges.pipe(takeUntil(this._onDestroy$)).subscribe(() => {
      this.autocompleteCtrl.setErrors(this.control.errors);
    });

    this.errors$ = getValidationErrorsChanges(this.control);

    const ctrlValues$ = this.control.valueChanges.pipe(
      startWith(this.control.value),
      distinctUntilChanged(),
    );
    const autocompleteValues$ = this.autocompleteCtrl.valueChanges.pipe(
      filter((input) => typeof input === 'string'),
      tap(() => this._setControlValue(null)),
      debounceTime(500),
      startWith(''),
      distinctUntilChanged(),
    );

    this.options$ = combineLatest([ctrlValues$, autocompleteValues$]).pipe(
      switchMap(([controlValue, userInput]) => {
        if (controlValue != null) {
          return this.config.getSingleOption(controlValue).pipe(
            tap((opt) => {
              if (opt != null) {
                this.autocompleteCtrl.setValue(opt);
              }
            }),
            map((opt) => {
              return opt != null ? [opt] : [];
            }),
          );
        } else if (typeof this.autocompleteCtrl.value !== 'string') {
          // if there is not an option selected and there is no user input
          this.autocompleteCtrl.setValue(null);
        }

        if (isNullOrWhitespace(userInput)) {
          return of([]);
        }

        return this.config.getManyOptions(userInput);
      }),
    );
    this._cd.detectChanges();
  }

  private _setControlValue(value: any, emitChange = true) {
    this.control.setValue(value, { emitEvent: emitChange });
    this._cd.markForCheck();
  }

  public onFocus = (event: any) => this._selectInputIfValueSet(event);

  public onOptionSelected = (event: MatAutocompleteSelectedEvent) => {
    const optionValue: any = event.option.value;
    const controlValue = this.config.selectControlValue(optionValue);
    this._setControlValue(controlValue);
  };

  private _allowDefault(code: string) {
    if (this.control.value == null) {
      return true;
    }
    const allowedPatterns: string[] = [
      'Key',
      'Digit',
      'Tab',
      'Backspace',
      'Delete',
      'Minus',
      'Comma',
      'Period',
      'Space',
      'Num',
    ];

    const match = allowedPatterns.find((pattern) => code.includes(pattern));

    return match != null;
  }

  public onKeyboardEvent(event: KeyboardEvent) {
    const code = event.code;
    if (!this._allowDefault(code)) {
      event.preventDefault();
    }
    this._selectInputIfValueSet(event);
  }

  public _selectInputIfValueSet(event: Event) {
    if (this.control.value != null) {
      (event.target as any).select();
    }
  }

  public optionDisplayFn = (value: any) => {
    if (value == null) {
      return '';
    }
    return this.config.optionDisplayFn(value);
  };

  public ngAfterViewInit() {}

  public ngOnDestroy() {
    this._onDestroy$.next();
  }
}
